import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import GetInTouchForm from '../GetinTouchform/GetinTouch';
import './herosection.css';
import useTheme from '../ColorConfig/usetheme';
import MobileHeroView from './HeroSub/MobileHero'; // Import the mobile component

// Import service icons
import squareIcon from '../assets/square.png';
import posIcon from '../assets/pos.png';
import digitalMarketIcon from '../assets/digitalmarket.png';
import blockchainIcon from '../assets/blockchain.png';

// Import hero images
import heroImage1 from '../assets/banner1.png';
import heroImage2 from '../assets/banner2.gif';

const HeroSection = () => {
    const colors = useTheme();
  const [bannerData, setBannerData] = useState(null);
  const [images, setImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previousImageIndex, setPreviousImageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [showGetInTouch, setShowGetInTouch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef(null);
  const leftContentRef = useRef(null);
  const rightContentRef = useRef(null);
  const navigate = useNavigate();
   const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const carouselRef = useRef(null);
  const minSwipeDistance = 50;

const serviceIcons = [
  { id: 1, icon: squareIcon, name: 'Customized App/Web', delay: 0 },
  { id: 2, icon: posIcon, name: 'Crypto (Web3)', delay: 0.2 },
  { id: 5, icon: digitalMarketIcon, name: 'Billing Software', delay: 0.4 },
  { id: 15, icon: blockchainIcon, name: 'E-Commerce', delay: 0.6 },
];

  // Hardcoded banner data
  const hardcodedBannerData = {
    id: "2",
    title: "Seamless Digital Transformation for the Modern Enterprise",
    description: "IDLBAY Pvt Ltd empowers businesses with innovative technology solutions, driving operational excellence, scalable growth, and digital evolution.",
    button1: "Contact Us",
    button2: "Learn More",
    totalImages: "2",
    image0: heroImage1,
    image1: heroImage2,
    textGradient: "Transformation"
  };

  // Hardcoded images array
  const hardcodedImages = [heroImage1, heroImage2];

  // Check if mobile on component mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 991);
    };

    // Initial check
    checkMobile();

    // Add event listener
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleServiceClick = (serviceId) => {
  navigate(`/servicedetails/${serviceId}`);
};

  useEffect(() => {
    // Commented out original API fetching function for future use
    /*
    const fetchBannerData = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.dremerz.net/api/DZ_8923_Idlbay_Banner_BannerDetails');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data && data.length > 0) {
          setBannerData(data[0]);
          
          // Extract images from the banner data
          const extractedImages = [];
          const totalImages = parseInt(data[0].totalImages) || 0;
          
          for (let i = 0; i < totalImages; i++) {
            const imageKey = `image${i}`;
            if (data[0][imageKey]) {
              extractedImages.push(`https://api.dremerz.net/uploads/${data[0][imageKey]}`);
            }
          }
          
          setImages(extractedImages);
          if (extractedImages.length > 0) {
            setPreviousImageIndex(extractedImages.length - 1);
          }
        } else {
          throw new Error('No banner data found');
        }
      } catch (err) {
        console.error('Error fetching banner data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBannerData();
    */

    // Load hardcoded data
    const loadHardcodedData = () => {
      try {
        setLoading(true);
        setBannerData(hardcodedBannerData);
        setImages(hardcodedImages);
        if (hardcodedImages.length > 0) {
          setPreviousImageIndex(hardcodedImages.length - 1);
        }
      } catch (err) {
        console.error('Error loading hardcoded banner data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadHardcodedData();
  }, []);

  const onTouchStart = useCallback((e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  }, []);

  const onTouchMove = useCallback((e) => {
    setTouchEnd(e.targetTouches[0].clientX);
    setIsDragging(true);
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe || isRightSwipe) {
      clearAutoAdvancement();
      
      if (isLeftSwipe) {
        setCurrentImageIndex(prevIndex => {
          const nextIndex = prevIndex === images.length - 1 ? 0 : prevIndex + 1;
          setPreviousImageIndex(prevIndex);
          return nextIndex;
        });
      } else {
        setCurrentImageIndex(prevIndex => {
          const prevIndexValue = prevIndex === 0 ? images.length - 1 : prevIndex - 1;
          setPreviousImageIndex(prevIndex);
          return prevIndexValue;
        });
      }
      
      restartAutoAdvancement();
    }
    
    setIsDragging(false);
    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, images.length]);

  // Mouse drag handlers for desktop
  const onMouseDown = useCallback((e) => {
    setTouchEnd(null);
    setTouchStart(e.clientX);
    setIsDragging(true);
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setTouchEnd(e.clientX);
  }, [isDragging]);

  const onMouseUp = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe || isRightSwipe) {
      clearAutoAdvancement();
      
      if (isLeftSwipe) {
        setCurrentImageIndex(prevIndex => {
          const nextIndex = prevIndex === images.length - 1 ? 0 : prevIndex + 1;
          setPreviousImageIndex(prevIndex);
          return nextIndex;
        });
      } else {
        setCurrentImageIndex(prevIndex => {
          const prevIndexValue = prevIndex === 0 ? images.length - 1 : prevIndex - 1;
          setPreviousImageIndex(prevIndex);
          return prevIndexValue;
        });
      }
      
      restartAutoAdvancement();
    }
    
    setIsDragging(false);
    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, images.length, isDragging]);

  const preventImageDrag = useCallback((e) => {
    e.preventDefault();
  }, []);

  let autoAdvanceInterval = useRef(null);

  const clearAutoAdvancement = useCallback(() => {
    if (autoAdvanceInterval.current) {
      clearInterval(autoAdvanceInterval.current);
      autoAdvanceInterval.current = null;
    }
  }, []);

  const restartAutoAdvancement = useCallback(() => {
    clearAutoAdvancement();
    autoAdvanceInterval.current = setInterval(() => {
      setCurrentImageIndex((prevIndex) => {
        const nextIndex = prevIndex === images.length - 1 ? 0 : prevIndex + 1;
        setPreviousImageIndex(prevIndex);
        return nextIndex;
      });
    }, 8000);
  }, [images.length]);

  useEffect(() => {
    if (images.length === 0) return;

    restartAutoAdvancement();

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      clearAutoAdvancement();
      observer.disconnect();
    };
  }, [images.length, clearAutoAdvancement, restartAutoAdvancement]);

  const handleContactClick = (e) => {
    e.preventDefault();
    setShowGetInTouch(true);
    document.body.style.overflow = 'hidden';
  };

  const closeGetInTouch = () => {
    setShowGetInTouch(false);
    document.body.style.overflow = 'auto';
  };

  const handleLearnMoreClick = (e) => {
    e.preventDefault();
    navigate('/about');
  };

  const handleIndicatorClick = (index) => {
    if (index !== currentImageIndex) {
      setPreviousImageIndex(currentImageIndex);
      setCurrentImageIndex(index);
    }
  };

  const getImageClassName = (index) => {
    if (index === currentImageIndex) {
      return 'idlbayhero-carousel-image idlbayhero-active';
    } else if (index === previousImageIndex) {
      return 'idlbayhero-carousel-image idlbayhero-previous';
    } else {
      return 'idlbayhero-carousel-image';
    }
  };

const renderTitle = () => {
  if (!bannerData || !bannerData.title) {
    return null;
  }
  
  const titleLines = bannerData.title.split('  ');
  const gradientText = bannerData.textGradient; // Get the gradient text from API
  
  return titleLines.map((line, index) => {
    // If there's gradient text and it exists in this line
    if (gradientText && line.includes(gradientText)) {
      const parts = line.split(gradientText);
      return (
        <React.Fragment key={index}>
          {parts[0]}
          <span className="idlbayhero-text-gradient idlbayhero-shimmer-animation">
            {gradientText}
          </span>
          {parts[1]}
          <br />
        </React.Fragment>
      );
    }
    
    // If no gradient text or gradient text not found in this line
    return (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    );
  });
};

  if (loading) {
    return (
      <section 
        className="idlbayhero-section idlbayhero-min-vh-100 idlbayhero-d-flex idlbayhero-align-items-center" 
        style={{ backgroundColor: `var(--color-bodyBlack)` }}
      >
        <div className="idlbayhero-container idlbayhero-py-3">
          <div className="idlbayhero-row idlbayhero-align-items-center">
            <div className="idlbayhero-col-12 idlbayhero-text-center">
              <div className="idlbayhero-spinner-border idlbayhero-text-primary" role="status">
                <span className="idlbayhero-visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !bannerData) {
    return (
      <section 
        className="idlbayhero-section idlbayhero-min-vh-100 idlbayhero-d-flex idlbayhero-align-items-center" 
        style={{ backgroundColor: `var(--color-bodyBlack)` }}
      >
        <div className="idlbayhero-container idlbayhero-py-3">
          <div className="idlbayhero-row idlbayhero-align-items-center">
            <div className="idlbayhero-col-12 idlbayhero-text-center">
              <p className="idlbayhero-text-white">Failed to load banner content. Please try again later.</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // If mobile, render the MobileHeroView component
  if (isMobile) {
    return (
      <>
        <MobileHeroView 
          bannerData={bannerData}
          images={images}
          currentImageIndex={currentImageIndex}
          handleContactClick={handleContactClick}
          handleLearnMoreClick={handleLearnMoreClick}
          handleIndicatorClick={handleIndicatorClick}
          renderTitle={renderTitle}
          handleServiceClick={handleServiceClick} 
        />

        {/* GetInTouch Modal */}
        {showGetInTouch && (
          <div className="idlbayhero-modal-overlay" onClick={closeGetInTouch}>
            <div className="idlbayhero-modal-content-custom" onClick={(e) => e.stopPropagation()}>
              <button className="idlbayhero-close-button" onClick={closeGetInTouch}>
                &times;
              </button>
              <GetInTouchForm isModal={true} />
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop view
  return (
    <>
      <section 
        className="idlbayhero-section idlbayhero-min-vh-100 idlbayhero-d-flex idlbayhero-align-items-center" 
        ref={sectionRef}
        style={{ backgroundColor: `var(--color-bodyBlack)` }}
      >
        {/* Animated Background */}
        <div className="idlbayhero-background-animation">
          <div className="idlbayhero-floating-shapes">
            <div className="idlbayhero-shape idlbayhero-shape-1"></div>
            <div className="idlbayhero-shape idlbayhero-shape-2"></div>
            <div className="idlbayhero-shape idlbayhero-shape-3"></div>
            <div className="idlbayhero-shape idlbayhero-shape-4"></div>
            <div className="idlbayhero-shape idlbayhero-shape-5"></div>
          </div>
          <div className="idlbayhero-particles">
            {Array.from({ length: 50 }).map((_, index) => (
              <div 
                key={index} 
                className="idlbayhero-particle" 
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 20}s`,
                  animationDuration: `${15 + Math.random() * 10}s`
                }}
              ></div>
            ))}
          </div>
        </div>

        <div className="idlbayhero-container">
          <div className="idlbayhero-row idlbayhero-align-items-center">
            {/* Left Content */}
            <div 
              className={`idlbayhero-col-lg-6 idlbayhero-pe-lg-5 idlbayhero-left-content ${isVisible ? 'idlbayhero-animate-from-left' : ''}`}
              ref={leftContentRef}
            >
              <div className="idlbayhero-mb-5">
                <div className="idlbayhero-service-icons-left">
                  {serviceIcons.map((service, index) => (
                    <div 
                      key={service.id}
                      className={`idlbayhero-service-icon idlbayhero-service-icon-${index + 1} ${isVisible ? 'idlbayhero-icon-animate' : ''}`}
                      style={{ animationDelay: `${service.delay + 1}s` }}
                      onClick={() => handleServiceClick(service.id)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleServiceClick(service.id);
                        }
                      }}
                    >
                      <img src={service.icon} alt={service.name} />
                      <div className="idlbayhero-service-tooltip">{service.name}</div>
                    </div>
                  ))}
                </div>

                <h2 className="idlbayhero-text fw-bold idlbayhero-display-4 idlbayhero-lh-1 idlbayhero-mb-4" style={{color: `var(--color-white)`}}>
                  {renderTitle()}
                </h2>
                
                <p className="idlbayhero-text idlbayhero-lead idlbayhero-mb-5" style={{color: `var(--color-white)`}}>
                  {bannerData.description}
                </p>
                
                <div className="d-flex flex-row flex-sm-row gap-3">
                  <button 
                    className="idlbayhero-btn idlbayhero-btn-primary idlbayhero-px-4 idlbayhero-py-3 idlbayhero-fw-semibold"
                    onClick={handleContactClick}
                  >
                    {bannerData.button1}
                  </button>
                  <button 
                    className="idlbayhero-btn idlbayhero-btn-outline-light idlbayhero-px-4 idlbayhero-py-3 idlbayhero-fw-semibold"
                    onClick={handleLearnMoreClick}
                  >
                    {bannerData.button2}
                  </button>
                </div>
              </div>
            </div>

            {/* Right Content - Only show on desktop */}
            {images.length > 0 && (
              <div 
                className={`idlbayhero-col-lg-6 idlbayhero-ps-lg-5 idlbayhero-right-content ${isVisible ? 'idlbayhero-animate-from-right' : ''}`}
                ref={rightContentRef}
              >
                <div className="idlbayhero-position-relative">
                  {/* Service Icons - Around Carousel */}
                  <div className="idlbayhero-carousel-service-icons">
                    <div className={`idlbayhero-carousel-icon idlbayhero-icon-top-left ${isVisible ? 'idlbayhero-icon-animate' : ''}`}>
                      <img src={squareIcon} alt="Square Solutions" />
                    </div>
                    <div className={`idlbayhero-carousel-icon idlbayhero-icon-top-right ${isVisible ? 'idlbayhero-icon-animate' : ''}`}>
                      <img src={posIcon} alt="POS Systems" />
                    </div>
                    <div className={`idlbayhero-carousel-icon idlbayhero-icon-bottom-left ${isVisible ? 'idlbayhero-icon-animate' : ''}`}>
                      <img src={digitalMarketIcon} alt="Digital Marketing" />
                    </div>
                  </div>

                  <div 
                    className="idlbayhero-image-carousel-container"
                    ref={carouselRef}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                    style={{ cursor: isDragging ? '' : '' }}
                  >
                    {images.map((image, index) => (
                      <div
                        key={index}
                        className={getImageClassName(index)}
                        onDragStart={preventImageDrag}
                      >
                        <img
                          src={image}
                          alt={`Technology ${index + 1}`}
                          className="idlbayhero-img-fluid idlbayhero-rounded-4 idlbayhero-shadow-lg"
                          onError={(e) => {
                            console.error(`Failed to load image: ${image}`);
                            e.target.style.display = 'none';
                          }}
                          draggable="false"
                        />
                      </div>
                    ))}
                  </div>
                  
                  <div className="idlbayhero-carousel-indicators">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        className={`idlbayhero-indicator ${index === currentImageIndex ? 'idlbayhero-active' : ''}`}
                        onClick={() => {
                          clearAutoAdvancement();
                          handleIndicatorClick(index);
                          restartAutoAdvancement();
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* GetInTouch Modal */}
      {showGetInTouch && (
        <div className="idlbayhero-modal-overlay" onClick={closeGetInTouch}>
          <div className="idlbayhero-modal-content-custom" onClick={(e) => e.stopPropagation()}>
            <button className="idlbayhero-close-button" onClick={closeGetInTouch}>
              &times;
            </button>
            <GetInTouchForm isModal={true} />
          </div>
        </div>
      )}
    </>
  );
};

export default HeroSection;